/**
 * FORGOT PASSWORD's body, shared by both trees (D-08, docs/EMAIL.md).
 *
 * NOT PORTED FROM A MIRROR, because there is no mirror to port. The design
 * prototype has no recovery at all — its accounts cannot outlive their crew and
 * a forgotten password is fixed by the owner reading out a new temp one. This
 * screen is built from Marcus's own words (2026-08-21) and owes a
 * DESIGN-CHANGELOG entry upstream; the type scale, colours and caps idiom are
 * the JOIN screen's, so it at least reads as the same product.
 *
 * The chrome differs between trees — the phone gets a full-bleed column, the
 * desktop a centred card — so, like `JoinCard`, the questions live here once
 * and each tree wraps them.
 */

import type { CSSProperties } from 'react';

import { useQd } from '../store/index.ts';
import { recoverVM } from '../store/selectors.ts';
import { PixelButton } from './PixelButton.tsx';

const MONO = 'var(--gt-font-mono)';

export interface RecoverMetrics {
  headFs: string;
  bodyFs: string;
  fieldFs: string;
  btnFs: number | string;
  pad: string;
}

const FIELD = (m: RecoverMetrics): CSSProperties => ({
  background: 'var(--gt-field)',
  border: '1px solid var(--gt-border-2)',
  color: 'var(--gt-text)',
  fontFamily: MONO,
  fontSize: m.fieldFs,
  padding: '12px 11px',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
});

export function RecoverCard({ m }: { m: RecoverMetrics }): JSX.Element {
  const s = useQd();
  const v = recoverVM(s);

  // SENT. Says what was done, never what was found — see `recoverVM`. There is
  // deliberately no "we couldn't find that account" branch to fall into.
  if (v.sent) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: m.pad }}>
        <div style={{ fontFamily: MONO, fontSize: m.headFs, color: 'var(--gt-text)', lineHeight: 1.6 }}>
          CHECK YOUR EMAIL
        </div>
        {/* No duration is quoted, deliberately. The link's life is GoTrue's
            `mailer_otp_exp`, a dashboard setting no code here can read — so a
            number printed here would be a guess that goes stale silently the
            first time somebody changes it. "SOON" is true at every value. */}
        <div style={{ fontFamily: MONO, fontSize: m.bodyFs, color: 'var(--gt-muted-2)', lineHeight: 1.9 }}>
          IF {v.sentTo.toUpperCase()} HAS AN ACCOUNT, A RESET LINK IS ON ITS WAY. OPEN IT SOON — IT
          EXPIRES.
        </div>
        {/* The accounts that predate email cannot be reached this way at all:
            their login address is still the synthesized one, at a domain with
            no MX. Nothing on this screen can tell that from a typo, so it names
            the person who CAN fix it rather than guessing. */}
        <div style={{ fontFamily: MONO, fontSize: '9px', color: 'var(--gt-muted)', lineHeight: 1.9 }}>
          NOTHING ARRIVING? CHECK YOUR SPAM FOLDER — AND IF YOUR PARTY LEADER ADDED YOU BEFORE YOU HAD
          AN EMAIL ON FILE, ASK THEM TO PUT YOUR ADDRESS ON YOUR ACCOUNT.
        </div>
        <PixelButton
          label="BACK TO LOG IN ▸"
          variant="outline"
          onClick={() => s.nav('login')}
          fs={m.btnFs}
          pad="15px 14px"
          center
        />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 13, padding: m.pad }}>
      <div style={{ fontFamily: MONO, fontSize: m.headFs, color: 'var(--gt-text)', lineHeight: 1.6 }}>
        FORGOT YOUR PASSWORD?
      </div>
      <div style={{ fontFamily: MONO, fontSize: m.bodyFs, color: 'var(--gt-muted-2)', lineHeight: 1.9 }}>
        ENTER THE EMAIL ADDRESS YOU SIGN IN WITH AND WE WILL SEND YOU A LINK TO SET A NEW ONE.
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
        <span style={{ fontFamily: MONO, fontSize: '8.5px', letterSpacing: '.08em', color: 'var(--gt-muted-2)' }}>
          EMAIL
        </span>
        <input
          type="email"
          autoComplete="email"
          value={v.fpEmail}
          onChange={(e) => s.setFpEmail(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.repeat) void s.sendReset();
          }}
          placeholder="you@example.com"
          style={FIELD(m)}
        />
      </div>

      {v.hasErr ? (
        <div style={{ fontFamily: MONO, fontSize: m.bodyFs, color: 'var(--acc2)', lineHeight: 1.8 }}>
          ✕ {v.fpErr}
        </div>
      ) : null}

      <PixelButton
        label={v.lbl}
        busy={v.busy}
        onClick={() => void s.sendReset()}
        fs={m.btnFs}
        pad="16px 14px"
        center
      />
      <div
        onClick={() => s.nav('login')}
        style={{
          fontFamily: MONO,
          fontSize: '9px',
          letterSpacing: '.05em',
          color: 'var(--gt-muted)',
          textAlign: 'center',
          cursor: 'pointer',
          paddingTop: 4,
        }}
      >
        BACK TO LOG IN
      </div>
    </div>
  );
}
